import { useTranslation } from "react-i18next";

import { captureEvent } from "@/lib/analytics/posthog";
import {
  useAcceptOfferMutation,
  useDeclineOfferMutation,
} from "@/queries/hooks/workerDashboardHooks";
import notify from "@/utils/notify";

type Opts = { onDone?: () => void };

/** Accept / decline an employer's shift offer, with toasts. Shared by Upcoming + the shift modal. */
export function useOfferResponse(source: "shift_modal" | "upcoming") {
  const { t } = useTranslation("dashboard");
  const accept = useAcceptOfferMutation();
  const decline = useDeclineOfferMutation();

  const onError = (err: unknown) =>
    notify.error(t("workerV2.offer.error", "Couldn't update this offer."), {
      description: err instanceof Error ? err.message : undefined,
    });

  return {
    accept: (shiftId: number, opts: Opts = {}) => {
      captureEvent("offer_response", { source, action: "accept" });
      accept.mutate(shiftId, {
        onSuccess: () => {
          notify.success(t("workerV2.offer.accepted", "Shift accepted — you're on it."));
          opts.onDone?.();
        },
        onError,
      });
    },
    decline: (shiftId: number, opts: Opts = {}) => {
      if (!window.confirm(t("workerV2.offer.declineConfirm", "Decline this shift offer?"))) return;
      captureEvent("offer_response", { source, action: "decline" });
      decline.mutate(shiftId, { onSuccess: () => opts.onDone?.(), onError });
    },
    isBusy: (shiftId: number | null | undefined) =>
      shiftId != null &&
      ((accept.isPending && String(accept.variables) === String(shiftId)) ||
        (decline.isPending && String(decline.variables) === String(shiftId))),
  };
}
